import { SERVICE_OPTIONS } from "../../utils/consts";
import {
  DisplayedPriceComponent,
  PriceParts,
  displayedMedicationPrice,
  isPriceComponentIncluded,
} from "../../utils/helpers";

/**
 * HL-11357: the discount actually taken off a Care+ cart.
 *
 * Mirrors the server's CartManager.getFinalPrice:
 *   bonusValue = subscriptionDiscount || (auto_bonus ? resolvedAutoBonus : bonus_value)
 * i.e. a subscription discount wins outright, otherwise the bonus applies. The auto-bonus cap is
 * already applied before this runs — CartManagerV2.getCartView sends the resolved amount as
 * cart.bonus_value — so the client only has to honour the precedence.
 *
 * Reading applied_subscription_discount alone is not enough since HL-11357: a plan that carries a
 * bonus pins that column to 0 and puts the money in bonus_value, so the Care+ cart card showed a
 * total the customer was never charged.
 */
export const resolveCarePlusDiscount = (cart: {
  applied_subscription_discount?: number | string | null;
  bonus_value?: number | string | null;
}): number => {
  const subscriptionDiscount = Number(cart?.applied_subscription_discount ?? 0) || 0;
  if (subscriptionDiscount > 0) return subscriptionDiscount;
  return Number(cart?.bonus_value ?? 0) || 0;
};

/** A Care+ PLAN order (subscription): only then are treatment + service bundled into the plan fee. */
export const isCarePlusPlanCart = (cart: { care_plus_plan_fee?: number | string | null }): boolean =>
  Number(cart.care_plus_plan_fee ?? 0) > 0;

/**
 * HL-11622: the Pro/Max plan fee already covers treatment and service for the whole period, so
 * those fees are never itemised or re-billed for it. Basic keeps them itemised.
 */
export const planCoversTreatmentAndService = (planFee: number, planTier: string | null | undefined): boolean =>
  planFee > 0 && (planTier === "pro" || planTier === "max");

const DEFAULT_CARE_PLUS_DISPLAYED_PRICE_COMPONENTS: DisplayedPriceComponent[] = ["official_price"];

export const resolveCarePlusPriceDisplay = (
  parts: PriceParts,
  displayedPriceComponents?: DisplayedPriceComponent[] | null,
): {
  medicationTotal: number;
  showTreatmentFee: boolean;
  showServiceFee: boolean;
  showShippingFee: boolean;
} => {
  const components = displayedPriceComponents?.length
    ? displayedPriceComponents
    : DEFAULT_CARE_PLUS_DISPLAYED_PRICE_COMPONENTS;

  return {
    medicationTotal: displayedMedicationPrice(parts, components),
    showTreatmentFee: !isPriceComponentIncluded(components, "treatment_fee"),
    showServiceFee: !isPriceComponentIncluded(components, "service_fee"),
    showShippingFee: !isPriceComponentIncluded(components, "shipping_fee"),
  };
};

export const resolveCarePlusShipping = (
  rawFee: number | string | null | undefined,
  waivedFees?: string[],
): { fee: number; originalFee?: number; isFree: boolean } => {
  const shippingFee = Number(rawFee ?? 0) || 0;
  const isWaived = shippingFee > 0 && Boolean(waivedFees?.includes("shipping_fee"));
  return {
    fee: isWaived ? 0 : shippingFee,
    originalFee: isWaived ? shippingFee : undefined,
    isFree: shippingFee === 0 || isWaived,
  };
};

/**
 * Today's and next month's delivery fee for the Care+ card.
 * - Self-pickup: nothing.
 * - Express: the quote the order will charge. ExpressDeliveryController persists it on
 *   cart.custom_shipping_fee without re-allocating product rows, so the product rows still carry the
 *   DHL config value and must not be read for express; they are only a fallback.
 * - Regular: cart.regular_shipping_fee (the live DHL fee), falling back to the product rows only
 *   when the API did not send it at all.
 */
export const resolveCarePlusShippingSchedule = ({
  selectedServiceOption,
  productShippingFee,
  regularShippingFee,
  expressShippingFee,
  breakdownShippingFee,
}: {
  selectedServiceOption?: string | null;
  productShippingFee: number;
  regularShippingFee?: number | string | null;
  expressShippingFee?: number | string | null;
  breakdownShippingFee?: number | null;
}): { todayFee: number; recurringFee: number } => {
  if (selectedServiceOption === SERVICE_OPTIONS.SELF_PICKUP) {
    return { todayFee: 0, recurringFee: 0 };
  }
  if (selectedServiceOption === SERVICE_OPTIONS.EXPRESS) {
    const expressFee = Number(expressShippingFee) || productShippingFee;
    return { todayFee: expressFee, recurringFee: expressFee };
  }
  const regularFee = Number(regularShippingFee ?? productShippingFee) || 0;
  return {
    todayFee: regularFee,
    recurringFee: Number(breakdownShippingFee ?? regularFee) || 0,
  };
};
